import { Button, Flex, Input } from 'antd';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Delete, PlusBlueWithoutBackground } from '@shared/assets/icons/components';
import type { PresetFormValues } from '../../lib/types';
import styles from './preset-form.module.scss';

type StringArrayPath =
  | 'content.quick_questions'
  | 'preview.static_answers'
  | 'advanced.contact_form.trigger_events';

interface DynamicStringListProps {
  control: Control<PresetFormValues>;
  name: StringArrayPath;
  placeholder: string;
  addLabel: string;
  disabled?: boolean;
}

export const DynamicStringList = ({
  control,
  name,
  placeholder,
  addLabel,
  disabled,
}: DynamicStringListProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor' });
  const { field } = useController({ control, name });
  const values = field.value as string[];

  const updateItem = (index: number, value: string) => {
    const nextValues = [...values];
    nextValues[index] = value;
    field.onChange(nextValues);
  };

  const removeItem = (index: number) => {
    field.onChange(values.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <Flex vertical gap={8} className={styles.dynamicList}>
      {values.map((value, index) => (
        <Flex key={`${name}-${index}`} gap={8} align="center">
          <Input
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onBlur={field.onBlur}
            onChange={(event) => updateItem(index, event.target.value)}
          />
          <Button
            danger
            type="text"
            icon={<Delete />}
            aria-label={t('removeItem')}
            disabled={disabled}
            className={styles.iconButton}
            onClick={() => removeItem(index)}
          />
        </Flex>
      ))}

      <Button
        type="dashed"
        icon={<PlusBlueWithoutBackground />}
        disabled={disabled}
        className={styles.addButton}
        onClick={() => field.onChange([...values, ''])}
      >
        {addLabel}
      </Button>
    </Flex>
  );
};
